import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Users, Loader2, MousePointer, Wand2, X, User } from 'lucide-react'
import {
  useListFacesQuery,
  useLazySearchByFaceQuery,
  useListPersonsQuery,
  useCreatePersonFromFacesMutation,
  useSoftDeleteFacesMutation,
  useClusterFacesMutation,
  useAcceptSuggestionMutation,
  useRejectSuggestionMutation,
  useUpdatePersonMutation,
  useUnlinkPersonMutation,
  useLazySearchByPersonQuery,
} from '@/store/api'
import type { FaceListItem } from '@/store/api'
import {
  startFaceSearch,
  startPersonSearch,
  setSearchResults,
  setSearchError,
} from '@/store/slices/searchSlice'
import {
  toggleSelectMode,
  toggleFaceSelection,
  selectFaceRange,
  clearFaceSelection,
  setViewMode,
  setClusterSuggestions,
  setIsClustering,
  removeSuggestion,
  clearSuggestions,
  setEditingFaceId,
  openFaceProfile,
  closeFaceProfile,
  openPersonProfile,
  closePersonProfile,
} from '@/store/slices/facesSlice'
import type { FacesViewMode } from '@/store/slices/facesSlice'
import type { RootState } from '@/store/store'
import { openImageModal } from '@/store/slices/uiSlice'
import FaceSelectionToolbar from '@/components/faces/FaceSelectionToolbar'
import PersonAssignDialog from '@/components/faces/PersonAssignDialog'
import PersonCard from '@/components/faces/PersonCard'
import DeletedFacesView from '@/components/faces/DeletedFacesView'
import ClusterSuggestionCard from '@/components/faces/ClusterSuggestionCard'
import FaceActionPopover from '@/components/faces/FaceActionPopover'
import FaceProfileModal from '@/components/faces/FaceProfileModal'
import PersonProfileModal from '@/components/faces/PersonProfileModal'
import { signedSrc } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

function FacesPage() {
  useSignedUrls()
  const dispatch = useDispatch()
  const [page, setPage] = useState(1)
  const [primaryOnly, setPrimaryOnly] = useState(false)
  const [searchingFaceId, setSearchingFaceId] = useState<string | null>(null)
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [isLoadingAll, setIsLoadingAll] = useState(false)
  const [popoverFaceId, setPopoverFaceId] = useState<string | null>(null)
  const [assignFaceId, setAssignFaceId] = useState<string | null>(null)
  const [allFaces, setAllFaces] = useState<FaceListItem[]>([])
  const prevDataRef = useRef<string | null>(null)
  const [dataGeneration, setDataGeneration] = useState(0)

  const {
    selectedFaceIds,
    isSelecting,
    lastSelectedIndex,
    viewMode,
    clusterSuggestions,
    isClustering,
    editingFaceId,
    profileFaceId,
    profilePersonId,
  } = useSelector((state: RootState) => state.faces)

  const { data, isLoading, isFetching } = useListFacesQuery({
    page,
    pageSize: 60,
    primaryOnly,
  })

  const { data: personsData } = useListPersonsQuery({ pageSize: 200 })

  const [triggerSearchByFace] = useLazySearchByFaceQuery()
  const [triggerSearchByPerson] = useLazySearchByPersonQuery()
  const [createPersonFromFaces] = useCreatePersonFromFacesMutation()
  const [softDeleteFaces] = useSoftDeleteFacesMutation()
  const [clusterFaces] = useClusterFacesMutation()
  const [acceptSuggestion] = useAcceptSuggestionMutation()
  const [rejectSuggestion] = useRejectSuggestionMutation()
  const [updatePerson] = useUpdatePersonMutation()
  const [unlinkPerson] = useUnlinkPersonMutation()

  const total = data?.total ?? 0
  const hasMore = data?.has_more ?? false
  const persons = personsData?.persons ?? []

  // Accumulate faces across pages (like LibraryPage does for images)
  useEffect(() => {
    if (data?.faces) {
      // Create a fingerprint to detect cache invalidation refetches
      const fingerprint = data.faces.map(f => f.face_id + (f.person_name || '')).join(',')
      if (page === 1) {
        setAllFaces(data.faces)
        prevDataRef.current = fingerprint
      } else if (fingerprint !== prevDataRef.current) {
        setAllFaces(prev => {
          const existingIds = new Set(prev.map(f => f.face_id))
          const newFaces = data.faces.filter(f => !existingIds.has(f.face_id))
          return [...prev, ...newFaces]
        })
        prevDataRef.current = fingerprint
      }
    }
  }, [data, page, dataGeneration])

  // Reset to page 1 when primaryOnly changes
  useEffect(() => {
    setPage(1)
    prevDataRef.current = null
    setDataGeneration(g => g + 1)
  }, [primaryOnly])

  // Named persons: one representative per person, alphabetically sorted
  const sortedPersons = useMemo(() => {
    return [...persons]
      .filter(p => p.name)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  }, [persons])

  // Unnamed faces: faces not assigned to any person
  const unnamedFaces = useMemo(() => {
    return allFaces.filter(f => !f.person_name)
  }, [allFaces])

  // Load All auto-continue
  useEffect(() => {
    if (isLoadingAll && !isFetching && hasMore) {
      setPage((p) => p + 1)
    }
    if (isLoadingAll && !hasMore) {
      setIsLoadingAll(false)
    }
  }, [isLoadingAll, isFetching, hasMore])

  // Face click: select mode or open profile (only for unnamed faces in All tab)
  const handleFaceClick = useCallback(
    (face: FaceListItem, index: number, shiftKey: boolean) => {
      if (isSelecting) {
        if (shiftKey && lastSelectedIndex !== null) {
          dispatch(
            selectFaceRange({
              faceIds: unnamedFaces.map((f) => f.face_id),
              fromIndex: lastSelectedIndex,
              toIndex: index,
            })
          )
        } else {
          dispatch(toggleFaceSelection({ faceId: face.face_id, index }))
        }
        return
      }

      // Browse mode: open face profile for unnamed faces
      dispatch(openFaceProfile(face.face_id))
    },
    [dispatch, isSelecting, lastSelectedIndex, unnamedFaces]
  )

  // Find similar from profile modal
  const handleFindSimilarFromProfile = useCallback(
    async (faceId: string, cropUrl: string) => {
      dispatch(closeFaceProfile())
      if (searchingFaceId) return
      setSearchingFaceId(faceId)
      dispatch(startFaceSearch({ faceId, cropUrl }))

      try {
        const results = await triggerSearchByFace({ faceId, limit: 20 }).unwrap()
        dispatch(setSearchResults(results))
      } catch (error: unknown) {
        const detail = (error as { data?: { detail?: string } })?.data?.detail ??
          (error instanceof Error ? error.message : null)
        dispatch(setSearchError(detail || 'No similar faces found'))
      } finally {
        setSearchingFaceId(null)
      }
    },
    [dispatch, triggerSearchByFace, searchingFaceId]
  )

  // Assign from profile modal
  const handleAssignFromProfile = useCallback((faceId: string) => {
    dispatch(closeFaceProfile())
    setAssignFaceId(faceId)
  }, [dispatch])

  // View source from profile modal — open ImageModal directly
  const handleViewSourceFromProfile = useCallback((masterId: string) => {
    dispatch(closeFaceProfile())
    dispatch(openImageModal({ masterId }))
  }, [dispatch])

  // Find images from person profile modal
  const handleFindImagesFromProfile = useCallback(
    async (personId: string) => {
      dispatch(closePersonProfile())
      const person = persons.find((p) => p.person_id === personId)
      dispatch(startPersonSearch({ personId, personName: person?.name ?? null }))

      try {
        const results = await triggerSearchByPerson({ personId, limit: 20 }).unwrap()
        dispatch(setSearchResults(results))
      } catch (error: unknown) {
        const detail = (error as { data?: { detail?: string } })?.data?.detail ??
          (error instanceof Error ? error.message : null)
        dispatch(setSearchError(detail || 'No images found for this person'))
      }
    },
    [dispatch, triggerSearchByPerson, persons]
  )

  // View individual face from person profile modal
  const handleViewFaceFromProfile = useCallback(
    (faceId: string) => {
      dispatch(openFaceProfile(faceId))
    },
    [dispatch]
  )

  // Inline naming
  const [inlineName, setInlineName] = useState('')

  const handleInlineNameSubmit = useCallback(
    async (face: FaceListItem) => {
      const name = inlineName.trim()
      if (!name) {
        dispatch(setEditingFaceId(null))
        return
      }

      try {
        if (face.person_id) {
          // Rename existing person
          await updatePerson({ personId: face.person_id, name }).unwrap()
        } else {
          // Create person from this face
          await createPersonFromFaces({ faceIds: [face.face_id], name }).unwrap()
        }
      } catch (e) {
        console.error('Failed to name face:', e)
      }
      dispatch(setEditingFaceId(null))
      setInlineName('')
    },
    [dispatch, inlineName, updatePerson, createPersonFromFaces]
  )

  // Selection toolbar handlers
  const handleGroupSelected = useCallback(() => {
    if (selectedFaceIds.length > 0) {
      setShowAssignDialog(true)
    }
  }, [selectedFaceIds])

  // Force re-sync with server data after mutations that change face metadata
  const invalidateAndRefresh = useCallback(() => {
    setPage(1)
    prevDataRef.current = null
    setDataGeneration(g => g + 1)
  }, [])

  const handleDeleteSelected = useCallback(async () => {
    if (selectedFaceIds.length === 0) return
    const toDelete = new Set(selectedFaceIds)
    await softDeleteFaces({ faceIds: selectedFaceIds }).unwrap()
    dispatch(clearFaceSelection())
    // Remove deleted faces from local state immediately
    setAllFaces(prev => prev.filter(f => !toDelete.has(f.face_id)))
  }, [dispatch, selectedFaceIds, softDeleteFaces])

  const handleCancelSelection = useCallback(() => {
    dispatch(clearFaceSelection())
    dispatch(toggleSelectMode())
  }, [dispatch])

  // Auto-clustering
  const handleAutoGroup = useCallback(async () => {
    dispatch(setIsClustering(true))
    try {
      const result = await clusterFaces({ threshold: 0.55 }).unwrap()
      dispatch(setClusterSuggestions(result.suggestions))
    } catch (e) {
      console.error('Clustering failed:', e)
      dispatch(setIsClustering(false))
    }
  }, [dispatch, clusterFaces])

  const handleAcceptSuggestion = useCallback(
    async (suggestionId: string, name?: string, faceIds?: string[]) => {
      try {
        await acceptSuggestion({ suggestionId, name, faceIds }).unwrap()
        dispatch(removeSuggestion(suggestionId))
        invalidateAndRefresh()
      } catch (e) {
        console.error('Accept failed:', e)
      }
    },
    [dispatch, acceptSuggestion, invalidateAndRefresh]
  )

  const handleRejectSuggestion = useCallback(
    async (suggestionId: string) => {
      try {
        await rejectSuggestion(suggestionId).unwrap()
        dispatch(removeSuggestion(suggestionId))
      } catch (e) {
        console.error('Reject failed:', e)
      }
    },
    [dispatch, rejectSuggestion]
  )

  // Person actions
  const handleFindImages = useCallback(
    async (personId: string) => {
      const person = persons.find((p) => p.person_id === personId)
      dispatch(startPersonSearch({ personId, personName: person?.name ?? null }))

      try {
        const results = await triggerSearchByPerson({ personId, limit: 20 }).unwrap()
        dispatch(setSearchResults(results))
      } catch (error: unknown) {
        const detail = (error as { data?: { detail?: string } })?.data?.detail ??
          (error instanceof Error ? error.message : null)
        dispatch(setSearchError(detail || 'No images found for this person'))
      }
    },
    [dispatch, triggerSearchByPerson, persons]
  )

  const handleRenamePerson = useCallback(
    async (personId: string, name: string) => {
      await updatePerson({ personId, name }).unwrap()
    },
    [updatePerson]
  )

  const handleMergePerson = useCallback((_personId: string) => {
    // TODO: merge dialog
  }, [])

  const handleRemoveFaceFromPerson = useCallback(
    async (faceId: string) => {
      try {
        await unlinkPerson({ faceIds: [faceId] }).unwrap()
        invalidateAndRefresh()
      } catch (e) {
        console.error('Failed to remove face from person:', e)
      }
    },
    [unlinkPerson, invalidateAndRefresh]
  )

  const handleMakePrimary = useCallback(
    async (personId: string, faceId: string) => {
      try {
        await updatePerson({ personId, prototype_face_id: faceId }).unwrap()
        invalidateAndRefresh()
      } catch (e) {
        console.error('Failed to set primary face:', e)
      }
    },
    [updatePerson, invalidateAndRefresh]
  )

  const handleDeleteSingleFace = useCallback(
    async (faceId: string) => {
      try {
        await softDeleteFaces({ faceIds: [faceId] }).unwrap()
        // Remove deleted face from local state immediately
        setAllFaces(prev => prev.filter(f => f.face_id !== faceId))
      } catch (e) {
        console.error('Failed to delete face:', e)
      }
    },
    [softDeleteFaces]
  )

  const handleLoadMore = useCallback(() => {
    setPage((p) => p + 1)
  }, [])

  // View mode tabs
  const viewModes: { key: FacesViewMode; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'grouped', label: 'Grouped' },
    { key: 'deleted', label: 'Deleted' },
  ]

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Faces</h1>
          {total > 0 && (
            <span className="text-sm text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
              {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {viewMode === 'all' && (
            <>
              <button
                onClick={handleAutoGroup}
                disabled={isClustering}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-secondary hover:bg-secondary/80 text-foreground transition-colors disabled:opacity-50"
              >
                {isClustering ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Wand2 size={14} />
                )}
                Auto-Group
              </button>
              <button
                onClick={() => dispatch(toggleSelectMode())}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  isSelecting
                    ? 'bg-cyan-600 text-white'
                    : 'bg-secondary hover:bg-secondary/80 text-foreground'
                }`}
              >
                <MousePointer size={14} />
                {isSelecting ? 'Selecting' : 'Select'}
              </button>
            </>
          )}
{/* Primary Only hidden — confusing UX, may reuse later */}
          {false && (
            <label
              className="flex items-center gap-2 text-sm cursor-pointer"
              title="Show only the best/primary face per image (hides secondary detections)"
            >
              <input
                type="checkbox"
                checked={primaryOnly}
                onChange={(e) => {
                  setPrimaryOnly(e.target.checked)
                }}
                className="rounded border-input"
              />
              Primary only
            </label>
          )}
        </div>
      </div>

      {/* View mode tabs */}
      <div className="flex gap-1 mb-4 border-b border-border">
        {viewModes.map((vm) => (
          <button
            key={vm.key}
            onClick={() => dispatch(setViewMode(vm.key))}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              viewMode === vm.key
                ? 'text-cyan-400 border-cyan-400'
                : 'text-muted-foreground border-transparent hover:text-foreground'
            }`}
          >
            {vm.label}
            {vm.key === 'grouped' && persons.length > 0 && (
              <span className="ml-1.5 text-xs bg-secondary px-1.5 py-0.5 rounded-full">
                {persons.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Cluster suggestions */}
      {clusterSuggestions.length > 0 && viewMode === 'all' && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-foreground">
              Suggested Groups ({clusterSuggestions.length})
            </h2>
            <button
              onClick={() => dispatch(clearSuggestions())}
              className="p-0.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              title="Dismiss all suggestions"
            >
              <X size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {clusterSuggestions.map((suggestion) => (
              <ClusterSuggestionCard
                key={suggestion.suggestion_id}
                suggestionId={suggestion.suggestion_id}
                faceIds={suggestion.face_ids}
                faceCount={suggestion.face_count}
                cropUrls={suggestion.crop_urls}
                representativeCropUrl={suggestion.representative_crop_url}
                avgSimilarity={suggestion.avg_similarity}
                onAccept={handleAcceptSuggestion}
                onReject={handleRejectSuggestion}
                onAssignFace={(faceId) => setAssignFaceId(faceId)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Deleted view */}
      {viewMode === 'deleted' && <DeletedFacesView />}

      {/* Grouped view */}
      {viewMode === 'grouped' && (
        <div>
          {persons.length === 0 && (
            <div className="flex flex-col items-center justify-center h-48 gap-4 text-center">
              <Users size={32} className="text-muted-foreground" />
              <div>
                <p className="text-sm font-medium mb-1">No persons created yet</p>
                <p className="text-xs text-muted-foreground">
                  Select faces on the All tab and group them, or use Auto-Group.
                </p>
              </div>
            </div>
          )}
          <div className="space-y-3">
            {persons.map((person) => (
              <PersonCard
                key={person.person_id}
                person={person}
                faces={allFaces}
                onFindImages={handleFindImages}
                onRename={handleRenamePerson}
                onMerge={handleMergePerson}
                onRemoveFace={handleRemoveFaceFromPerson}
                onMakePrimary={handleMakePrimary}
                onViewFace={(faceId) => dispatch(openFaceProfile(faceId))}
              />
            ))}
          </div>

          {/* Ungrouped faces section */}
          {allFaces.filter((f) => !f.person_id).length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                Ungrouped ({allFaces.filter((f) => !f.person_id).length})
              </h3>
              <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-4">
                {allFaces
                  .filter((f) => !f.person_id)
                  .map((face, idx) => (
                    <div key={face.face_id} className="relative">
                      <FaceThumbnail
                        face={face}
                        index={idx}
                        isSelecting={false}
                        isSelected={false}
                        isSearching={searchingFaceId === face.face_id}
                        isEditing={editingFaceId === face.face_id}
                        inlineName={inlineName}
                        onInlineNameChange={setInlineName}
                        onInlineNameSubmit={() => handleInlineNameSubmit(face)}
                        onClick={() => setPopoverFaceId(
                          popoverFaceId === face.face_id ? null : face.face_id
                        )}
                        onStartEditing={() => {
                          setInlineName(face.person_name ?? '')
                          dispatch(setEditingFaceId(face.face_id))
                        }}
                      />
                      {popoverFaceId === face.face_id && (
                        <FaceActionPopover
                          onName={() => {
                            setInlineName(face.person_name ?? '')
                            dispatch(setEditingFaceId(face.face_id))
                          }}
                          onAssign={() => setAssignFaceId(face.face_id)}
                          onFindSimilar={() => handleFaceClick(face, idx, false)}
                          onDelete={() => handleDeleteSingleFace(face.face_id)}
                          onClose={() => setPopoverFaceId(null)}
                        />
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* All view */}
      {viewMode === 'all' && (
        <>
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <Loader2 size={32} className="animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading faces...</p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && unnamedFaces.length === 0 && sortedPersons.length === 0 && (
            <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-secondary text-muted-foreground rounded-full">
                <Users size={32} />
              </div>
              <div>
                <p className="text-sm font-medium mb-1">No faces found</p>
                <p className="text-xs text-muted-foreground">
                  Faces are detected automatically during image processing.
                </p>
              </div>
            </div>
          )}

          {/* Person Representatives — one tile per named person */}
          {sortedPersons.length > 0 && (
            <div className="mb-2">
              <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-4">
                {sortedPersons.map((person) => (
                  <div
                    key={person.person_id}
                    className="flex flex-col items-center gap-1.5 cursor-pointer group"
                    onClick={() => dispatch(openPersonProfile(person.person_id))}
                  >
                    <div className="relative w-16 h-16 rounded-full border-2 border-border group-hover:border-cyan-500 transition-colors overflow-hidden bg-secondary">
                      {person.prototype_crop_url ? (
                        <img
                          src={signedSrc(person.prototype_crop_url)}
                          alt={person.name ?? 'Unknown'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User size={24} className="text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <User size={16} className="text-white" />
                      </div>
                    </div>
                    <span className="text-xs text-cyan-400 text-center truncate w-full" title={person.name ?? undefined}>
                      {person.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Separator between persons and unnamed faces */}
          {sortedPersons.length > 0 && unnamedFaces.length > 0 && (
            <div className="border-t border-border my-4" />
          )}

          {/* Unnamed Face Grid */}
          {unnamedFaces.length > 0 && (
            <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-4">
              {unnamedFaces.map((face, idx) => (
                <FaceThumbnail
                  key={face.face_id}
                  face={face}
                  index={idx}
                  isSelecting={isSelecting}
                  isSelected={selectedFaceIds.includes(face.face_id)}
                  isSearching={searchingFaceId === face.face_id}
                  isEditing={editingFaceId === face.face_id}
                  inlineName={inlineName}
                  onInlineNameChange={setInlineName}
                  onInlineNameSubmit={() => handleInlineNameSubmit(face)}
                  onClick={(shiftKey: boolean) => handleFaceClick(face, idx, shiftKey)}
                  onStartEditing={() => {
                    setInlineName(face.person_name ?? '')
                    dispatch(setEditingFaceId(face.face_id))
                  }}
                />
              ))}
            </div>
          )}

          {/* Load More / Load All */}
          {hasMore && allFaces.length < total && (
            <div className="flex justify-center gap-3 mt-8">
              <button
                onClick={handleLoadMore}
                disabled={isFetching || isLoadingAll}
                className="flex items-center gap-2 px-6 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {isFetching && !isLoadingAll && <Loader2 size={16} className="animate-spin" />}
                Load More
              </button>
              <button
                onClick={() => setIsLoadingAll(true)}
                disabled={isFetching && !isLoadingAll}
                className="flex items-center gap-2 px-6 py-2 bg-secondary hover:bg-secondary/80 text-foreground rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {isLoadingAll ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Loading... ({allFaces.length} of {total})
                  </>
                ) : (
                  'Load All'
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Selection toolbar */}
      {isSelecting && selectedFaceIds.length > 0 && (
        <FaceSelectionToolbar
          count={selectedFaceIds.length}
          onGroup={handleGroupSelected}
          onDelete={handleDeleteSelected}
          onCancel={handleCancelSelection}
        />
      )}

      {/* Person assign dialog (multi-select) */}
      {showAssignDialog && (
        <PersonAssignDialog
          faceIds={selectedFaceIds}
          onClose={() => setShowAssignDialog(false)}
          onSuccess={() => {
            setShowAssignDialog(false)
            dispatch(clearFaceSelection())
            dispatch(toggleSelectMode())
          }}
        />
      )}

      {/* Person assign dialog (single face from popover) */}
      {assignFaceId && (
        <PersonAssignDialog
          faceIds={[assignFaceId]}
          onClose={() => setAssignFaceId(null)}
          onSuccess={() => setAssignFaceId(null)}
        />
      )}

      {/* Face profile modal */}
      {profileFaceId && (
        <FaceProfileModal
          faceId={profileFaceId}
          onClose={() => dispatch(closeFaceProfile())}
          onFindSimilar={handleFindSimilarFromProfile}
          onAssign={handleAssignFromProfile}
          onViewSource={handleViewSourceFromProfile}
        />
      )}

      {/* Person profile modal */}
      {profilePersonId && (
        <PersonProfileModal
          personId={profilePersonId}
          onClose={() => dispatch(closePersonProfile())}
          onFindImages={handleFindImagesFromProfile}
          onViewFace={handleViewFaceFromProfile}
        />
      )}
    </div>
  )
}

// Face thumbnail component
interface FaceThumbnailProps {
  face: FaceListItem
  index: number
  isSelecting: boolean
  isSelected: boolean
  isSearching: boolean
  isEditing: boolean
  inlineName: string
  onInlineNameChange: (name: string) => void
  onInlineNameSubmit: () => void
  onClick: (shiftKey: boolean) => void
  onStartEditing: () => void
}

function FaceThumbnail({
  face,
  isSelecting,
  isSelected,
  isSearching,
  isEditing,
  inlineName,
  onInlineNameChange,
  onInlineNameSubmit,
  onClick,
  onStartEditing,
}: FaceThumbnailProps) {
  return (
    <div
      onClick={(e) => onClick(e.shiftKey)}
      className="flex flex-col items-center gap-1.5 cursor-pointer group"
    >
      <div
        className={`relative w-16 h-16 rounded-full border-2 transition-colors overflow-hidden bg-secondary ${
          isSelected
            ? 'border-cyan-500'
            : 'border-border group-hover:border-cyan-500'
        }`}
      >
        {isSearching ? (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 size={18} className="animate-spin text-cyan-500" />
          </div>
        ) : (
          <img
            src={signedSrc(face.crop_url)}
            alt={`Face ${face.face_id}`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}

        {/* Selection checkmark */}
        {isSelecting && isSelected && (
          <div className="absolute inset-0 bg-cyan-500/30 flex items-center justify-center">
            <div className="w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">✓</span>
            </div>
          </div>
        )}

        {/* Hover overlay */}
        {!isSelecting && (
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <User size={16} className="text-white" />
          </div>
        )}
      </div>

      {/* Name / confidence display */}
      {isEditing ? (
        <input
          type="text"
          value={inlineName}
          onChange={(e) => onInlineNameChange(e.target.value)}
          onBlur={onInlineNameSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onInlineNameSubmit()
            if (e.key === 'Escape') onInlineNameSubmit()
          }}
          className="text-xs w-full px-1 py-0.5 rounded bg-secondary border border-cyan-500 text-foreground text-center focus:outline-none"
          autoFocus
          onClick={(e) => e.stopPropagation()}
        />
      ) : face.person_name ? (
        <span
          className="text-xs text-cyan-400 text-center truncate w-full cursor-text"
          onClick={(e) => {
            e.stopPropagation()
            onStartEditing()
          }}
          title={`${face.person_name} (click to rename)`}
        >
          {face.person_name}
        </span>
      ) : (
        <span
          className="text-xs text-muted-foreground text-center truncate w-full"
          onClick={(e) => {
            if (!isSelecting) {
              e.stopPropagation()
              onStartEditing()
            }
          }}
          title="Click to name this face"
        >
          {Math.round(face.detection_confidence * 100)}%
        </span>
      )}
    </div>
  )
}

export default FacesPage
